/**
 * @file history.tab.tsx
 * @description Компонент табы "История" для формы баг-репорта. 
 * Отображает историю изменений статусов баг-репорта с временными метками, комментариями и информацией о модификаторах.
 * Загружает данные истории через ObjectFormService при первом рендере и отображает их в виде карточек.
 * Показывает переходы между статусами с визуальными индикаторами и поддерживает комментарии к изменениям.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies [react, react-hook-form, @consta/uikit/Text, @consta/icons/IconForward, @api/actions, @utils/date-time-helper, @components/icons/user-circle, @components/ui/indicators/state-badge, @src/types]
 * @related-files [@api/actions/object-form.ts, @utils/date-time-helper.ts, @components/icons/user-circle/user-circle.component.tsx, @components/ui/indicators/state-badge/state-badge.component.tsx]
 */

import { tabProps } from '@src/types';
import { ObjectFormService } from '@api/actions';
import { useEffect } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { Text } from '@consta/uikit/Text';
import { IconForward } from '@consta/icons/IconForward';
import { formatTimestamp } from '@utils/date-time-helper';
import { UserCircleIcon } from '@components/icons/user-circle';
import { StateBadge } from '@components/ui/indicators/state-badge';

export const History = ({ control, data, tab, getValues, setValue }: tabProps) => {
    useEffect(() => {
        if (getValues && !getValues(tab || 'histories')) {
            getPageData();
        }
    }, [getValues, tab]);

    const getPageData = async () => {
        if (!data?.item) {
            return;
        }

        try {
            const response = await ObjectFormService.get(data.item.$type, data.item.id, tab);

            if (response && setValue) {
                setValue(tab || 'histories', response);
            }
        } catch (error) {
            console.error('Failed to fetch page data:', error);
        }
    };

    return (
        <Controller
            name={tab || ''}
            control={control}
            render={({ field }: { field: FieldValues }) => (
                <div className="flex flex-col gap-2">
                    {Boolean(field.value?.length) &&
                        field.value?.map((item: any, key: number) => (
                            <div
                                className="border items-center bg-white rounded-[0.8468vmin] p-[1.0585vmin] pb-[0.3177vmin] text-[#002033]"
                                key={key}
                            >
                                <div className="flex gap-default items-center h-[3.4947vmin]">
                                    <Text size="s">{formatTimestamp(item.modifiedAt, 'DD.MM.YYYY HH:mm')}</Text>

                                    <div className="flex items-center">
                                        <Text size="s" className="mr-[1.0585vmin]">
                                            Изменён статус:
                                        </Text>

                                        <StateBadge
                                            state={item.statePrevious}
                                            className="mr-[0.6961vmin] h-[1.9054vmin]"
                                        />

                                        <IconForward size="xs" className="mr-[0.6961vmin]" />

                                        <StateBadge state={item.stateCurrent} className="h-[1.9054vmin]" />
                                    </div>
                                </div>

                                <div className="flex items-center gap-[0.4234vmin]">
                                    <UserCircleIcon />
                                    <Text size="s">{item.modifiedBy}</Text>
                                </div>

                                {item.comment ? (
                                    <Text className="py-[0.7413vmin]" size="s">
                                        {item.comment}
                                    </Text>
                                ) : (
                                    <Text className="py-[0.7413vmin]" size="s" view="ghost">
                                        Нет комментария
                                    </Text>
                                )}
                            </div>
                        ))}
                </div>
            )}
        />
    );
};
